import { useLineItemsListSections } from "@/pages/(app)/returns/refund/hooks/use-line-items-list-sections";
import useLineItemsQuantitiesSummary from "@/pages/(app)/returns/refund/hooks/use-line-items-quantities-summary";
import {
  useNotReturned,
  useSetNotReturned,
} from "@/pages/(app)/returns/refund/hooks/use-line-items-state-context";
import { useRefundLineItem } from "@/pages/(app)/returns/refund/hooks/use-refund-line-items-context";
import { ReturnRequestLineItemDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@babackapp/ui/components/accordion";
import { Badge } from "@babackapp/ui/components/badge";
import NumberFlow from "@number-flow/react";
import { ArticleItem } from "../article-item/article-item";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useCurrentReturnRequest } from "@/pages/(app)/returns/utils/current-return-request-context";
import useGetItemsInActiveReturns from "@babackapp/private-api-client/client/use-get-items-in-active-returns";
import { useConflictingItems } from "@/pages/(app)/returns/hooks/use-conflicting-items";
import { ConflictWarning } from "@/pages/(app)/returns/components/conflict-warning";

export default function ArticlesNotReturned() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const returnRequest = useCurrentReturnRequest();
  const { itemsNotReturned } = useLineItemsListSections();

  const summary = useLineItemsQuantitiesSummary({ state: "not-returned" });

  const { data: queryData } = useGetItemsInActiveReturns({
    shopId: shop.id,
    returnRequestId: returnRequest.id,
  });

  const itemsInActiveReturns = queryData;

  if (!itemsNotReturned.length) {
    return null;
  }

  return (
    <AccordionItem value="notReturned">
      <AccordionTrigger>
        <span>
          {t("returnDetail.confirmModal.notReturned")}
          <Badge variant="secondary" className="ml-2">
            <NumberFlow value={summary.selected} suffix={` / ${summary.max}`} />
          </Badge>
        </span>
      </AccordionTrigger>
      <AccordionContent>
        <div className="border-border border rounded-md flex-col gap-4 divide-y overflow-hidden">
          {itemsNotReturned.map((lineItem: ReturnRequestLineItemDto) => (
            <ArticleItemNotReturned
              key={lineItem.line_item_id}
              lineItem={lineItem}
              itemsInActiveReturns={itemsInActiveReturns}
            />
          ))}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

const ArticleItemNotReturned = ({
  lineItem,
  itemsInActiveReturns,
}: {
  lineItem: ReturnRequestLineItemDto;
  itemsInActiveReturns?: { itemsInOtherReturns: Array<{ lineItemId: string; quantity: number; returnRequestId: string; returnRequestName: string; }> };
}) => {
  const { data: shop } = useCurrentShopSuspense();
  const { item, updateResolution, updateVariant } = useRefundLineItem(lineItem);
  const quantityValue = useNotReturned(lineItem.line_item_id);
  const updateQuantity = useSetNotReturned(lineItem.line_item_id);

  const {
    conflictingItems,
    adjustedMaxQuantity,
    isDisabled,
    warningMessage,
  } = useConflictingItems({
    lineItemId: lineItem.line_item_id,
    itemsInActiveReturns: itemsInActiveReturns?.itemsInOtherReturns,
    currentQuantity: quantityValue.selectedQuantity,
    maxQuantity: quantityValue.maxQuantity,
  });

  const hasConflict = conflictingItems.length > 0;

  const uniqueReturns = Array.from(
    new Map(conflictingItems.map(item => [item.returnRequestId, item])).values()
  );

  const displayMessage = hasConflict && warningMessage ? (
    <ConflictWarning
      warningMessage={warningMessage}
      conflictingReturns={uniqueReturns}
      adjustedMaxQuantity={adjustedMaxQuantity}
      shopId={shop.id}
    />
  ) : undefined;

  /**
  When an article is not returned, it has no resolution.
  When the merchant adds an article to the request, we need to add a resolution to it.
  So if the quantity is 0, remove the resolution, otherwise add it, default to refund
  */
  const _updateQuantity = (quantity: number) => {
    if (quantity === 0) updateResolution(null);
    else if (!item.resolution) updateResolution("refund");
    updateQuantity(quantity);
  };

  return (
    <ArticleItem
      lineItem={item}
      quantity={quantityValue.selectedQuantity}
      maxQuantity={adjustedMaxQuantity}
      onChangeQuantity={_updateQuantity}
      onChangeVariant={updateVariant}
      onChangeResolution={updateResolution}
      hideStatus
      hideResolution={quantityValue.selectedQuantity === 0}
      disabled={isDisabled}
      disabledReason={displayMessage}
    />
  );
};
